// Sticky page header for People Insights: breadcrumb, title, and the
// header-level filter row. Status, Pillar, Country, BU, Gender, Tenure
// and Work Mode all live as multi-select dropdown chips so users can
// apply filters without drilling into a section first. Pure
// presentation; all state lives in the parent (driven by `useHrStats`).

import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GroupsIcon from '@mui/icons-material/Groups';

import type {
  HrDimension,
  HrEmployeeStatus,
  HrFilterMap,
} from '../../types/hrStats';
import { ACCENTS } from './primitives/peopleInsightsTokens';
import FilterDropdownChip from './primitives/FilterDropdownChip';

const STATUS_OPTIONS: Array<{ value: HrEmployeeStatus; label: string }> = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'BOTH', label: 'All' },
];

const STATUS_LABEL_BY_VALUE: Record<HrEmployeeStatus, string> = STATUS_OPTIONS.reduce(
  (acc, o) => {
    acc[o.value] = o.label;
    return acc;
  },
  {} as Record<HrEmployeeStatus, string>,
);

/**
 * Header-level filter chips. Order matches the user-facing screenshot.
 * "status" is a special chip backed by `employeeStatus`, not the filter map.
 */
const HEADER_FILTER_DIMENSIONS: Array<{ id: HrDimension; label: string }> = [
  { id: 'pillar', label: 'Pillar' },
  { id: 'country', label: 'Country' },
  { id: 'business_unit', label: 'Business Unit' },
  { id: 'gender', label: 'Gender' },
  { id: 'tenure_bucket', label: 'Tenure' },
  { id: 'work_mode', label: 'Work Mode' },
];

export interface PeopleInsightsHeaderProps {
  employeeStatus: HrEmployeeStatus;
  onChangeStatus: (status: HrEmployeeStatus) => void;
  filters: HrFilterMap;
  /** Replace the selected values for a given dimension. Empty array = clear. */
  onApplyFilter: (dim: HrDimension, values: string[]) => void;
  onRemoveFilter: (dim: HrDimension) => void;
  onClearAll: () => void;
  /** Tagline shown beneath the title. Defaults to a static description. */
  subtitle?: string;
  /** Snapshot date label, shown at the right end of the filter row. */
  asOfLabel?: string;
}

const PeopleInsightsHeader: React.FC<PeopleInsightsHeaderProps> = ({
  employeeStatus,
  onChangeStatus,
  filters,
  onApplyFilter,
  onRemoveFilter,
  onClearAll,
  subtitle = 'Headcount, demographics, and squad analytics across all business units',
  asOfLabel,
}) => {
  const indigo = ACCENTS.indigo;
  const filterEntries = Object.entries(filters) as Array<[HrDimension, string[]]>;
  const hasFilters =
    filterEntries.some(([, vs]) => vs.length > 0) || employeeStatus !== 'ACTIVE';

  

  const valuesFor = (dim: HrDimension): string[] => filters[dim] ?? [];

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderBottom: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 5,
      }}
    >
      {/* Top row: breadcrumb + title */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ color: 'text.secondary', mb: 0.25 }}
          >
            <Typography variant="caption">Delivery Pulse</Typography>
            <ChevronRightIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
              People
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                backgroundColor: indigo.surface,
                color: indigo.on,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <GroupsIcon fontSize="small" />
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, letterSpacing: '-0.01em', color: 'text.primary' }}
            >
              People Insights
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Filter row: dropdown chips + as-of label */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.25,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#F8FAFC',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            mr: 0.5,
          }}
        >
          Filters
        </Typography>

        {/* Pillar (1st), Country, Business Unit — placed before Status to match
            the supplied design. */}
        <FilterDropdownChip
          label="Pillar"
          dimension="pillar"
          pageFilters={filters}
          employeeStatus={employeeStatus}
          currentValues={valuesFor('pillar')}
          onApply={(values) => {
            if (values.length === 0) onRemoveFilter('pillar');
            else onApplyFilter('pillar', values);
          }}
        />
        <FilterDropdownChip
          label="Country"
          dimension="country"
          pageFilters={filters}
          employeeStatus={employeeStatus}
          currentValues={valuesFor('country')}
          onApply={(values) => {
            if (values.length === 0) onRemoveFilter('country');
            else onApplyFilter('country', values);
          }}
        />
        <FilterDropdownChip
          label="Business Unit"
          dimension="business_unit"
          pageFilters={filters}
          employeeStatus={employeeStatus}
          currentValues={valuesFor('business_unit')}
          onApply={(values) => {
            if (values.length === 0) onRemoveFilter('business_unit');
            else onApplyFilter('business_unit', values);
          }}
        />

        {/* Status — fixed three-option chip, not a drill filter. Single-pick
            because Status is mutually exclusive (Active vs Inactive vs All).
            We model "ACTIVE" as the cleared/default state so the chip looks
            unfiltered until the user actively changes it. */}
        <FilterDropdownChip
          label="Status"
          multiple={false}
          staticOptions={STATUS_OPTIONS.map((s) => ({
            value: s.value,
            label: s.label,
          }))}
          currentValues={employeeStatus === 'ACTIVE' ? [] : [employeeStatus]}
          labelMap={STATUS_LABEL_BY_VALUE as Record<string, string>}
          onApply={(values) => {
            // Single-pick: take the first value, or fall back to ACTIVE.
            const next = (values[0] as HrEmployeeStatus | undefined) ?? 'ACTIVE';
            onChangeStatus(next);
          }}
        />

        {/* Remaining drill dimensions. */}
        {HEADER_FILTER_DIMENSIONS.filter(
          (d) => !['pillar', 'country', 'business_unit'].includes(d.id),
        ).map((d) => (
          <FilterDropdownChip
            key={d.id}
            label={d.label}
            dimension={d.id}
            pageFilters={filters}
            employeeStatus={employeeStatus}
            currentValues={valuesFor(d.id)}
            onApply={(values) => {
              if (values.length === 0) onRemoveFilter(d.id);
              else onApplyFilter(d.id, values);
            }}
          />
        ))}

        {hasFilters && (
          <Box
            component="button"
            onClick={() => {
              onClearAll();
              onChangeStatus('ACTIVE');
            }}
            sx={{
              border: 'none',
              backgroundColor: 'transparent',
              color: indigo.on,
              fontWeight: 600,
              fontSize: '0.75rem',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PeopleInsightsHeader;
